import { useMemo, useState } from "react";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import HeaderPage from "../../../shared/components/HeaderPage/HeaderPage";
import IntegrationsAddContent from "./IntegrationsAddContent";
import IntegrationCredentialsModal from "./IntegrationCredentialsModal";
import { useIntegrationsOptions } from "./useIntegrationsOptions";

const filterOptions = [
  { label: "Todas", value: "all" },
  { label: "Configuradas", value: "configured" },
  { label: "Disponíveis", value: "available" },
];

function IntegrationsPageAdd() {
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [filterValue, setFilterValue] = useState("all");

  const { integrationsList, loadingRegistered, refresh } = useIntegrationsOptions((integration) => {
    setSelectedIntegration(integration);
    setOpenModal(true);
  });

  const activeFilter = filterOptions.find((option) => option.value === filterValue);
  const filteredIntegrations = useMemo(() => {
    if (filterValue === "configured") {
      return integrationsList.filter((integration) => integration.hasCredential);
    }

    if (filterValue === "available") {
      return integrationsList.filter((integration) => !integration.hasCredential);
    }

    return integrationsList;
  }, [filterValue, integrationsList]);

  const handleFilterSelect = (value) => {
    setFilterValue(value);
    setFilterAnchor(null);
  };

  return (
    <>
      <HeaderPage
        variant="listing"
        title="Integrações"
        description="Conecte sistemas de cobrança, ERPs e serviços externos à sua operação."
        actions={
          <>
            <IconButton
              aria-label="Recarregar integrações"
              onClick={refresh}
              disabled={loadingRegistered}
              sx={{
                backgroundColor: "#fff",
                border: "1px solid #d6cfff",
                borderRadius: "999px",
                boxShadow: "0 8px 18px rgba(15, 23, 42, 0.035)",
                color: "#5f48f6",
                height: 40,
                minWidth: 72,
                "&:hover": {
                  backgroundColor: "#f7f4ff",
                  borderColor: "#bcaeff",
                },
              }}
            >
              <RefreshRoundedIcon
                sx={{
                  fontSize: 20,
                  transition: "transform 180ms ease",
                  transform: loadingRegistered ? "rotate(120deg)" : "none",
                }}
              />
            </IconButton>

            <Button
              variant="outlined"
              startIcon={<FilterListRoundedIcon />}
              onClick={(event) => setFilterAnchor(event.currentTarget)}
              sx={{
                backgroundColor: "#fff",
                borderColor: "#cbd6e2",
                borderRadius: "999px",
                color: "#334155",
                fontWeight: 700,
                minHeight: 40,
                px: 2,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#f8fafc",
                  borderColor: "#94a3b8",
                },
              }}
            >
              {activeFilter?.label || "Filtrar"}
            </Button>

            <Menu
              anchorEl={filterAnchor}
              open={Boolean(filterAnchor)}
              onClose={() => setFilterAnchor(null)}
              PaperProps={{
                sx: {
                  border: "1px solid #e2e8f0",
                  borderRadius: "12px",
                  boxShadow: "0 16px 32px rgba(15, 23, 42, 0.12)",
                  mt: 1,
                  minWidth: 168,
                },
              }}
            >
              {filterOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  selected={option.value === filterValue}
                  onClick={() => handleFilterSelect(option.value)}
                  sx={{
                    fontSize: 14,
                    fontWeight: option.value === filterValue ? 700 : 500,
                  }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Menu>
          </>
        }
      />
      <IntegrationsAddContent templateData={filteredIntegrations} />
      <IntegrationCredentialsModal
        open={openModal}
        integration={selectedIntegration}
        onClose={() => setOpenModal(false)}
      />
    </>
  );
}

export default IntegrationsPageAdd;
